<script setup lang="ts">
import {computed, defineProps, onMounted, ref} from 'vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormCalendar from '@/components/inputs/FormCalendar.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import {Icon} from '@iconify/vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import TableFilter from '@/components/table/TableFilter.vue'
import {getBatchList} from '@/api/batches'
import {usePackingListForm} from '@/composables/usePackingListForm'
import {useRouter} from 'vue-router'
import {addOrderPackingList} from '@/api/packingList'

const props = defineProps<{
  visible: boolean
  comformityOptions: any[]
  orderedProductsOptions: any[]
  rolls: any[]
  close: () => void
  orderNumber: string | null
}>()

const router = useRouter()
const orderId = router.currentRoute.value.params.id
const { values, handleSubmit, setErrors, showToast, loading } = usePackingListForm('add')

const selectAll = ref(false)
const batchList = ref<any>([])
const selectedRolls = ref<any[]>([])

const getBatchNumber = (batchId: string | undefined) => {
  return batchList.value.find((batch: any) => batch.id === batchId)?.batchNumber
}

const filteredRolls = computed(() => {
  if (!values.orderedProductId) {
    return [];
  }

  const selectedOrderProduct = props.orderedProductsOptions.find(
    (product) => product.id === values.orderedProductId
  )

  return props.rolls.filter(roll =>
  roll.finishingQualityId === selectedOrderProduct?.qualityId &&
  roll.rollState === 3
)
})

const totalMetersOrdered = computed(() => {
  const selectedOrderProduct = props.orderedProductsOptions.find(
    (product) => product.id === values.orderedProductId
  )
  const totalMetersOrdered = selectedOrderProduct?.totalLengthValue
  return totalMetersOrdered || 0
})

const totalRollsOrdered = computed(() => {
  return filteredRolls.value.length
})

const totalMetersSelected = ref(0)
// Toggle selection for all rolls
const toggleSelectAll = () => {
  selectAll.value = !selectAll.value
  if (selectAll.value) {
    // Select all rolls and update values.rolls
    selectedRolls.value = [...filteredRolls.value]
    totalMetersSelected.value = selectedRolls.value.reduce((acc, roll) => acc + roll.lengthValue, 0)
  } else {
    // Deselect all rolls
    selectedRolls.value = []
    totalMetersSelected.value = 0
  }
}

const randomPackingListId = `PLID-${Math.floor(Math.random() * 1000)}`
// Toggle selection for an individual roll
const toggleRollSelection = (roll: any) => {
  const index = selectedRolls.value.findIndex((r) => r.id === roll.id)
  if (index > -1) {
    selectedRolls.value.splice(index, 1)
    totalMetersSelected.value -= roll.lengthValue
  } else {
    selectedRolls.value.push(roll)
    totalMetersSelected.value += roll.lengthValue
  }
  selectAll.value = selectedRolls.value.length === filteredRolls.value.length
}

const savePackingList = handleSubmit(async () => {
  loading.value = true
  const formValues = {
    ...values,
    orderId: orderId,
    packingListId: randomPackingListId,
    packingListDate: values.packingListDate,
    orderedProductId: values.orderedProductId,
    rollIds: selectedRolls.value.map((roll) => roll.id),
  }
  if (
    !formValues.packingListDate ||
    (typeof formValues.packingListDate === 'object' &&
      Object.keys(formValues.packingListDate).length === 0)
  ) {
    showToast('error', 'Error', 'Please select a date for the packing list.')
    loading.value = false
    return
  }
  await addOrderPackingList(formValues)
    .then((response) => {
      loading.value = false
      showToast('success', 'Packing List Added!', 'You have successfully added a new packing list.')
      props.close()
      const packingListId = response?.id

      if (packingListId) {
        router.push(`/packingList/${packingListId}`)
      }
    })
    .catch((error) => {
      console.error(error)
      setErrors(error)
      loading.value = false
      showToast('error', 'Error!', 'Something went wrong. Please try again.')
    })
})

onMounted(async () => {
  batchList.value = await getBatchList()
})
</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '70rem' }" :closable="false">
    <div class="px-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl">
          Customer Order: <span class="font-bold">{{ orderNumber }}</span>
        </h3>
      </div>
      <form @submit.prevent="savePackingList">
        <div class="flex justify-between flex-row w-full gap-16 my-8">
          <FormInputText
            label="Packing List ID"
            inputName="packingListId"
            id="packingListId"
            placeholder="This field will be pre-filled by the system"
            :disabled="true"
            class="w-1/2"
          />
          <FormCalendar
            label="Packing List Date"
            inputName="packingListDate"
            id="packingListDate"
            placeholder="Enter packing list date"
            class="w-1/2"
            v-model="values.packingListDate"
          />
        </div>
        <div class="flex justify-start flex-col">
          <div>
            <h3 class="text-lg font-bold mb-2">Ordered Products</h3>
            <p class="text-sm">
              You can choose what rolls of a specific product to add to a Packing List. <br />
              Only <strong>Products ordered by the customer </strong> will appear in the dropdown
              menu.
            </p>
          </div>
          <div class="my-8">
            <FormDropdown
              label="Ordered Products"
              inputName="orderedProductId"
              id="orderedProductId"
              :options="orderedProductsOptions"
              optionLabel="finishedReference"
              optionValue="id"
              placeholder="Choose Products in this order"
              class="w-1/2"
              v-model="values.orderedProductId"
            />
            <p class="text-sm mt-2">
              Choosing a specific product from the list of products the <br />
              customer has ordered <strong>filters the rolls </strong> shown.
            </p>
          </div>
        </div>
        <div class="">
          <DataTable
            :value="filteredRolls"
            paginator
            :rows="10"
            tableStyle="min-width: 50rem"
            stripedRows
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            :rowsPerPageOptions="[10, 25]"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
            :pt-options="{
              mergeProps: true
            }"
            class="w-full"
          >
            <template #header>
              <div class="flex justify-between font-normal">
                <p>
                  Rolls Available: <span class="font-bold">{{ filteredRolls.length }}</span>
                </p>
                <div class="flex flex-col gap-3 items-end">
                  <div class="flex flex-col gap-1 items-end">
                    <p class="text-sm text-yellow-400 mb-2">
                      Total Metres Ordered:
                      <span class="bg-yellow-200 text-neutral-900 rounded p-1">{{ totalMetersOrdered }}</span>
                    </p>
                    <p class="text-sm">
                      Metres Selected:
                      <span class="bg-gray-200 text-neutral-900 rounded p-1">{{
                        totalMetersSelected
                      }}</span>
                    </p>
                  </div>
                </div>
                <TableFilter />
              </div>
            </template>
            <Column field="select">
              <template #header>
                <div>
                  <input
                    type="checkbox"
                    name="selectAll"
                    id="selectAll"
                    :checked="selectAll"
                    @change="toggleSelectAll"
                  />
                  <label for="selectAll" class="ml-2">Select All</label>
                </div>
              </template>
              <template #body="slotProps">
                <div class="flex flex-row">
                  <input
                    type="checkbox"
                    name="select"
                    id="select-{{ slotProps.data.id }}"
                    :checked="selectedRolls.includes(slotProps.data)"
                    @change="toggleRollSelection(slotProps.data)"
                  />
                  <label for="selectAll" class="ml-2">Select roll</label>
                </div>
              </template>
            </Column>
            <Column field="rollNumber" header="Roll Number" />
            <Column field="batchId" header="Batch Number">
              <template #body="slotProps">
                {{ getBatchNumber(slotProps.data.batchId) }}
              </template>
            </Column>
            <Column field="weight" header="Weight" />
            <Column field="lengthValue" header="Length" />
          </DataTable>
        </div>
      </form>
    </div>
    <template #footer>
      <div class="flex justify-end gap-4 gap-x-4">
        <Button severity="contrast" class="p-button-danger bg-gray-300" @click="close">
          <span>Cancel</span>
          <Icon
            icon="material-symbols:close-rounded"
            class="text-xl ml-3 order-1 bg-white rounded"
          />
        </Button>
        <Button
          severity="contrast"
          label="Save Packing List"
          class="p-button-danger bg-neutral-900 text-white"
          @click="savePackingList"
        />
      </div>
    </template>
  </Dialog>
</template>
